/**
 * Componente VideoPreview
 *
 * O que faz:
 * - Exibe um preview de vídeo com estado de "processando" (loading) enquanto o backend/n8n processa.
 * - Quando a URL estiver disponível, mostra o player HTML5 com controles para reprodução.
 * - Suporta thumbnail/poster quando disponível e fallback visual durante processamento.
 *
 * Onde é usado:
 * - Utilizado no ChatWindow para renderizar mensagens do tipo vídeo ("video").
 *
 * Como se conecta:
 * - Recebe videoUrl (string opcional), fileName (string) e isLoading (boolean) para controlar o estado visual.
 * - Usa player HTML5 nativo para máxima compatibilidade com diferentes formatos de vídeo.
 */
import React from 'react';
import { Loader2, Play, Video as VideoIcon } from 'lucide-react';

interface VideoPreviewProps {
  videoUrl?: string;
  fileName: string;
  isLoading?: boolean;
  thumbnailUrl?: string; // URL opcional de thumbnail/poster
}

export const VideoPreview: React.FC<VideoPreviewProps> = ({
  videoUrl,
  fileName,
  isLoading = false,
  thumbnailUrl
}) => {
  return (
    <div className="relative inline-block max-w-[70vw] sm:max-w-md w-full">
      {/* Overlay de carregamento enquanto processa */}
      {isLoading && (
        <div className="absolute inset-0 bg-muted/70 rounded-lg flex items-center justify-center z-10">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="ml-2 text-xs text-muted-foreground">Processando...</span>
        </div>
      )}

      {videoUrl ? (
        // Player de vídeo HTML5 quando URL está disponível
        <video
          className="rounded-lg w-full max-w-md max-h-72 object-contain bg-muted"
          controls
          controlsList="nodownload"
          preload="metadata"
          playsInline
          poster={thumbnailUrl} // Usar thumbnail se disponível
        >
          <source src={videoUrl} type="video/mp4" />
          <source src={videoUrl} type="video/webm" />
          <source src={videoUrl} type="video/avi" />
          <source src={videoUrl} type="video/mov" />
          Seu navegador não suporta reprodução de vídeo.
        </video>
      ) : (
        // Fallback visual enquanto não temos URL (durante processamento)
        <div className="bg-muted p-6 rounded-lg flex flex-col items-center gap-3 w-full max-w-md min-h-[200px] justify-center">
          <div className="relative">
            <VideoIcon className="w-12 h-12 text-muted-foreground" />
            {!isLoading && (
              <Play className="w-6 h-6 text-primary absolute -bottom-1 -right-1 bg-background rounded-full p-1" />
            )}
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground truncate max-w-full">{fileName}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {isLoading ? 'Processando...' : 'Aguardando processamento'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPreview;